/**
 * Raw channel escape hatch.
 *
 * Subscribes to an arbitrary list of channels at an arbitrary
 * namespace and retains a bounded buffer of events. The subscription
 * resumes across serial runs, so the buffer keeps accumulating events
 * for the lifetime of the thread (use `extensionProjection` instead when
 * you only need the most-recent payload of a single `custom:<name>`
 * channel). Consumers that need assembly semantics should use
 * `messagesProjection`, `toolCallsProjection`, etc. instead; this one is
 * for inspection, custom reducers, or niche use-cases.
 */
import type { Channel, Event } from "@langchain/protocol";
import { inferChannel } from "../../client/stream/subscription.js";
import type { ProjectionSpec, ProjectionRuntime } from "../types.js";
import { isRootNamespace, namespaceKey } from "../namespace.js";
import { openProjectionSubscription } from "./runtime.js";

/** Max events retained per raw subscription. Older events are dropped. */
const DEFAULT_BUFFER = 4096;

export interface ChannelProjectionOptions {
  /**
   * Maximum number of events retained in the projection snapshot.
   * Defaults to 4096 so replay-backed discovery hooks can tolerate
   * bursty token/media streams without dropping early lifecycle events.
   */
  bufferSize?: number;
  /**
   * Whether to open a real subscription and receive replayed history.
   * Defaults to true. Set false for live-only root-bus inspection when
   * replay is unnecessary.
   */
  replay?: boolean;
}

export function channelProjection(
  channels: readonly Channel[],
  namespace: readonly string[],
  options: ChannelProjectionOptions = {}
): ProjectionSpec<Event[]> {
  const chs = [...channels].sort();
  const ns = [...namespace];
  const bufferSize = options.bufferSize ?? DEFAULT_BUFFER;
  const replay = options.replay ?? true;
  const key = `channel|${bufferSize}|${replay ? "replay" : "live"}|${chs.join(",")}|${namespaceKey(ns)}`;

  return {
    key,
    namespace: ns,
    initial: [],
    open({ thread, store, rootBus }): ProjectionRuntime {
      // If this projection is scoped to the root namespace AND every
      // requested channel is already covered by the controller's root
      // pump, attach to the shared fan-out instead of opening a
      // second server subscription. This is the common case for
      // lightweight event-trace / debug panels.
      const covered =
        !replay &&
        isRootNamespace(ns) &&
        chs.every((c) => rootBus.channels.includes(c));

      if (covered) {
        const requestedSet = new Set(chs as Channel[]);
        // Classify via `inferChannel` so namespaced methods (e.g.
        // `input.requested` → `input`) and named custom events
        // (`custom` + `data.name` → `custom:<name>`) match the same
        // way as the slow path's `matchesSubscription`. Comparing
        // `event.method` to channel names would silently drop `input`.
        const matches = (event: Event): boolean => {
          const channel = inferChannel(event);
          if (channel === undefined) return false;
          return (
            requestedSet.has(channel) ||
            (channel.startsWith("custom:") && requestedSet.has("custom"))
          );
        };
        const push = (event: Event): void => {
          if (!matches(event)) return;
          const current = store.getSnapshot();
          const next =
            current.length >= bufferSize
              ? [...current.slice(current.length - bufferSize + 1), event]
              : [...current, event];
          store.setValue(next);
        };
        const unsubscribe = rootBus.subscribe(push);
        return {
          dispose() {
            unsubscribe();
          },
        };
      }

      return openProjectionSubscription({
        thread,
        channels: chs as Channel[],
        namespace: ns,
        // Keep the buffer alive across serial runs. Some transports pause a
        // subscription on each run's terminal lifecycle event; without
        // resuming, the channel would go silent after the first run and a
        // second prompt on the same thread would emit no further events.
        resumeOnPause: true,
        onEvent(event) {
          const current = store.getSnapshot();
          const next =
            current.length >= bufferSize
              ? [...current.slice(current.length - bufferSize + 1), event]
              : [...current, event];
          store.setValue(next);
        },
      });
    },
  };
}
